import React, { createContext, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import moment from 'moment-timezone';
import dayjs from 'dayjs';
import dayjsUtcPlugin from 'dayjs/plugin/utc';
import HelperMethods from '../services/exports/HelperMethods';
import TimezoneManager from '../services/localization/TimezoneManager';
import ReduxHooks from '../store/ReduxHooks';
import { actionCreators } from '../store/actions';

export const HelpersContext = createContext(null);

export default function HelpersProvider({ children }) {
  const { company } = useSelector((store) => store.currentCompany);
  const { serviceProvider } = useSelector((state) => state.currentServiceProvider);
  const { paths } = useSelector((store) => store.activeTab);

  dayjs.extend(dayjsUtcPlugin);

  const regionalConfig = company?.regional_config ?? serviceProvider?.regional_config;

  const currencySymbol = HelperMethods.getCurrencySymbol(
    company?.currency ?? serviceProvider?.currency,
    regionalConfig?.number_locale,
  );
  const formatCurrency = useCallback(
    (amount, currency = null, locale = null) =>
      HelperMethods.formatCurrency(
        amount,
        currency ?? company?.currency ?? serviceProvider?.currency,
        locale ?? regionalConfig?.number_locale,
      ),
    [company?.currency, serviceProvider?.currency, regionalConfig?.number_locale],
  );
  const getCurrencySymbol = useCallback(
    (currency = null, locale = null) =>
      HelperMethods.getCurrencySymbol(
        currency ?? company?.currency ?? serviceProvider?.currency,
        locale ?? regionalConfig?.number_locale,
      ),
    [company?.currency, serviceProvider?.currency, regionalConfig?.number_locale],
  );

  const timezone = company?.timezone ?? serviceProvider?.timezone;

  // The moment-token equivalent of the region's date_format, for callers that
  // need the format STRING itself rather than an already-formatted value —
  // e.g. a DatePicker's `format` prop, which both displays and parses input.
  const dateInputFormat = HelperMethods.cldrToMoment(regionalConfig?.date_format ?? 'dd-MM-yyyy');

  // The outlet's 12h/24h preference (PROD-475), falling back to the hour cycle its
  // region declares when nobody has chosen one.
  const outletTimePattern = useMemo(
    () => TimezoneManager.getTimePattern(company?.time_format, regionalConfig?.hour_cycle),
    [company?.time_format, regionalConfig?.hour_cycle],
  );

  /**
   * Render in the outlet's own timezone, not the browser's — a reseller managing
   * Eastern and Central locations from one seat must see each one's local clock.
   * Parse first, convert second: `moment(x).tz(z)` moves an instant into the zone,
   * where `moment.tz(x, z)` would reinterpret a naive string as being in it.
   *
   * Provider-level screens have no outlet, so they fall back to the provider's own
   * timezone rather than to the browser's: a reseller's staff sit in several zones
   * and must read one shared clock, not each their own. The final unzoned branch is
   * reached only before initial data lands — and even then `InitialDataProvider`
   * has usually already called `moment.tz.setDefault()`, so it is rarely literally
   * the browser's zone either.
   */
  const inOutletTimezone = useCallback(
    (datetime) => (timezone ? moment(datetime).tz(timezone) : moment(datetime)),
    [timezone],
  );

  const formatDate = useCallback(
    (datetime, format = undefined) => inOutletTimezone(datetime).format(format ?? dateInputFormat),
    [dateInputFormat, inOutletTimezone],
  );

  const formatTime = useCallback(
    (datetime, format = undefined) => {
      const _format = format ?? outletTimePattern;

      return inOutletTimezone(datetime).format(_format);
    },
    [outletTimePattern, inOutletTimezone],
  );

  const formatDateTime = useCallback(
    (datetime, { format = undefined, dateFormat = undefined, timeFormat = undefined } = {}) => {
      const _dateFormat = dateFormat ?? dateInputFormat;
      const _timeFormat = timeFormat ?? outletTimePattern;
      const _format = format ?? `${_dateFormat} ${_timeFormat}`.trim();

      return inOutletTimezone(datetime).format(_format);
    },
    [dateInputFormat, outletTimePattern, inOutletTimezone],
  );

  const formatDateTimeRange = useCallback(
    (start, end, options = {}) => {
      const startLabel = formatDateTime(start, options);

      if (!start || !end) {
        return startLabel;
      }

      return `${startLabel} - ${formatTime(end, options.timeFormat)}`;
    },
    [formatDateTime, formatTime],
  );

  const getActiveTab = useCallback(
    (path, defaultTab) => {
      try {
        return paths[path ?? window.location.pathname] ?? defaultTab;
      } catch {
        return defaultTab;
      }
    },
    [paths],
  );

  const setActiveTab = useCallback(
    (tab, path) => ReduxHooks.dispatch(actionCreators.activeTab.set({ path: path ?? window.location.pathname, tab })),
    [paths],
  );

  const context = useMemo(
    () => ({
      formatCurrency,
      getCurrencySymbol,
      formatDate,
      formatTime,
      formatDateTime,
      formatDateTimeRange,
      getActiveTab,
      setActiveTab,
      currencySymbol,
      dateInputFormat,
      timezone,
    }),
    [
      formatCurrency,
      getCurrencySymbol,
      formatDate,
      formatTime,
      formatDateTime,
      formatDateTimeRange,
      getActiveTab,
      setActiveTab,
      currencySymbol,
      dateInputFormat,
      timezone,
    ],
  );

  return <HelpersContext.Provider value={context}>{children}</HelpersContext.Provider>;
}
